
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { theme } from 'styled-tools';
import { useFiltersDisclosures } from 'src/context/filters-disclosures/context';
import Disclosure from 'src/components/disclosure';
import React, { ReactElement, ReactNode } from 'react';
import Type from 'src/components/core/typography/index';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  className?: string,
  name: string,
  selectedFilters?: string[] | null,
  title: string
};

/**
 * `Header` styled component.
 */

const Header = styled.div`
  display: grid;
  grid-gap: ${units(2)};
  grid-template-columns: 100%;
  width: 100%;

  ${media.min('ms')`
    grid-template-columns: max-content 1fr;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  padding: ${units(3)} ${units(3)} ${units(3)} ${units(1.5)};
  position: relative;
`;

/**
 * `Tags` styled component.
 */

const Tags = styled.div`
  ${theme('typography.styles.p')}

  align-items: center;
  color: ${color('grey300')};
  display: flex;
  flex-flow: row wrap;
  padding-right: ${units(2)};

  > * {
    margin-bottom: ${units(1)};
    margin-right: ${units(1)};
  }
`;

/**
 * `FilterDisclosure` component.
 */

const FilterDisclosure = (props: Props): ReactElement => {
  const { children, className, name, selectedFilters, title } = props;
  const { getDisclosureState, onToggle } = useFiltersDisclosures();

  return (
    <Disclosure
      className={className}
      header={(
        <Header>
          <Type.Paragraph as={'div'}>
            {title}
          </Type.Paragraph>

          {!isEmpty(selectedFilters) && (
            <Tags className={'filters-disclosure-tags'}>
              {map(selectedFilters, (filter, index: number) => (
                <span key={index}>
                  {filter}
                </span>
              ))}
            </Tags>
          )}
        </Header>
      )}
      isOpen={getDisclosureState(name)}
      scrollOnOpen
      toggle={() => onToggle(name)}
    >
      <Content>
        {children}
      </Content>
    </Disclosure>
  );
};

/**
 * Export `FilterDisclosure` component.
 */

export default FilterDisclosure;
